// JSON-function first-arg column affinity (Workstream C, oracle 18.§1).
//
// `JSON_VALUE(p.▮, '$.cpu')` — the first argument names the JSON document
// column, but a plain alias-dot listing ranks all columns alphabetically and
// buries it. Two honest signals lift the right column:
//   1. columns this BUFFER already treats as JSON (used as a JSON_* first
//      arg elsewhere — the caret's own token counts, mirroring S4),
//   2. columns the caller knows to be JSON (bus classification or MetaInfo
//      declaration) — supplied via the `knownJsonColumns` union.
//
// Engine-agnostic text walk — wired in oracle today; parity candidate for
// the other engines' JSON_VALUE/JSON_QUERY surfaces.

import type { CompletionCandidate } from '@/core/engines';

// Default function set fits Oracle exactly and MSSQL adequately. Other engines
// pass their own dialect list (sqlite `json_extract|json_each|…`, mysql
// `JSON_EXTRACT|JSON_UNQUOTE|…`, pgsql `jsonb_path_query|json_extract_path|…`).
const DEFAULT_JSON_FN_NAMES: readonly string[] = ['JSON_VALUE', 'JSON_QUERY', 'JSON_EXISTS', 'JSON_TABLE'];

function fnAlternation(fnNames: readonly string[]): string {
  return fnNames.join('|');
}

export function isAtJsonFunctionFirstArg(
  text: string,
  offset: number,
  statementStart: number,
  fnNames: readonly string[] = DEFAULT_JSON_FN_NAMES,
): boolean {
  const re = new RegExp(`\\b(?:${fnAlternation(fnNames)})\\s*\\(\\s*[\\w."$]*$`, 'i');
  return re.test(text.slice(statementStart, offset));
}

/** Lower-cased column tokens used as a JSON-function first argument anywhere in
 *  the buffer (alias-qualified or bare), for the given engine's function set. */
export function collectBufferJsonFunctionColumns(
  text: string,
  fnNames: readonly string[] = DEFAULT_JSON_FN_NAMES,
): Set<string> {
  const re = new RegExp(
    `\\b(?:${fnAlternation(fnNames)})\\s*\\(\\s*(?:[A-Za-z_][\\w$]*\\s*\\.\\s*)?([A-Za-z_][\\w$]*)`,
    'gi',
  );
  const cols = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    cols.add(m[1].toLowerCase());
  }
  return cols;
}

/**
 * Lift matching column candidates to tier 1; the rest of the tier-1 column
 * block drops to 2. Comparison is lower-cased on `filterText ?? label`.
 * No-op when no candidate matches (a wrong guess must not reshuffle).
 */
export function applyJsonFunctionArgPromotion(
  out: CompletionCandidate[],
  jsonColumnsLower: ReadonlySet<string>,
): void {
  if (jsonColumnsLower.size === 0) return;
  if (
    !out.some(
      (c) =>
        c.kind === 'column' &&
        jsonColumnsLower.has((c.filterText ?? c.label).toLowerCase()),
    )
  ) {
    return;
  }
  for (let i = 0; i < out.length; i += 1) {
    const c = out[i];
    if (c.kind !== 'column') continue;
    if (jsonColumnsLower.has((c.filterText ?? c.label).toLowerCase())) {
      if (c.tier !== 1) out[i] = { ...c, tier: 1 };
    } else if (c.tier === 1) {
      out[i] = { ...c, tier: 2 };
    }
  }
}
